import { useState } from 'react';

import { useCentralizedProviderData } from '@crehana/auth';
import { Button } from '@crehana/compass.ui/button';
import { Loader } from '@crehana/compass.ui/loader';
import { Input } from '@crehana/compass.ui/v3/input';
import { toast } from '@crehana/compass.ui/v3/toaster';
import {
  PasswordRulesList,
  PasswordStrengthBar,
  useFetchPasswordRules,
} from '@crehana/compass.web';
import {
  FeatureFlagProvider,
  useFeatureFlag,
  useFeatureFlagStore,
} from '@crehana/feature-flags/react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { useForm, zodResolver } from '@crehana/forms/rhf';
import { useTranslation } from '@crehana/i18n';
import { useDebounceValue } from '@crehana/react-hooks';

import { FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH } from './feature-flags';
import {
  useChangeMyPassword,
  useChangePassword,
} from './hooks/use-change-password';
import { useLearningSession } from './hooks/use-learning-session';
import { type TChangePasswordSchema, useChangePasswordSchema } from './schema';

const ALL_STRENGTH_LABELS_ORDER = [
  'CHANGE_PASSWORD_STRENGTH_VERY_WEAK',
  'CHANGE_PASSWORD_STRENGTH_WEAK',
  'CHANGE_PASSWORD_STRENGTH_MEDIUM',
  'CHANGE_PASSWORD_STRENGTH_STRONG',
] as const;

type TChangePasswordFormProps = {
  onSuccess: () => void;
  organizationId?: string;
};

type TLearningSessionData = {
  userLearningId: string;
  organizationLearningId: string;
};

type TChangePasswordFormContentProps = TChangePasswordFormProps & {
  learningSessionData?: TLearningSessionData;
};

const ChangePasswordFormContent = ({
  onSuccess,
  learningSessionData,
}: TChangePasswordFormContentProps) => {
  const { t } = useTranslation();

  const [rejectedPassword, setRejectedPassword] = useState<string | undefined>(
    undefined,
  );

  // The force-change-password screen renders in a pre/partial-auth context,
  // so security.current_session.centralized_session.centralized_organization_id
  // is null (there's no full session yet). currentPageOrganization is resolved
  // from the URL slug instead, so it's available here regardless of auth state.
  const { currentPageOrganization } = useCentralizedProviderData();
  const centralizedOrgId = currentPageOrganization?.id;

  // Feature flag — must be resolved before schema/form initialization
  const {
    flagValue: isStrengthEnabled,
    loading: flagLoading,
    error: flagErrorRaw,
  } = useFeatureFlag(
    FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH[0],
    FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH[1],
  );
  const flagError = Boolean(flagErrorRaw);
  const flagStore = useFeatureFlagStore();
  const handleFlagRetry = () => {
    flagStore.fetchFlag(
      FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH[0],
      FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH[1],
    );
  };

  const schema = useChangePasswordSchema(t, isStrengthEnabled);
  const form = useForm<TChangePasswordSchema>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const { formState, trigger, getValues } = form;
  const { isValid, isSubmitting } = formState;

  const { changePassword } = useChangePassword();
  const { changeMyPassword } = useChangeMyPassword();

  // Password rules — always fetched, only used when flag is on.
  // We pass the centralized org id explicitly (resolved from the URL slug via
  // useCentralizedProviderData above) because the hook's session-based fallback
  // (security.current_session.centralized_session.centralized_organization_id)
  // is null pre-auth, which left the rules list empty/hidden on this screen.
  // Falls back to the hook's own session lookup when centralizedOrgId is
  // unavailable (e.g. no organization slug in the URL), preserving prior
  // behavior for that case.
  const { rules, loading: rulesLoading } = useFetchPasswordRules({
    organizationId: centralizedOrgId ? String(centralizedOrgId) : undefined,
  });

  const passwordValue = form.watch('password');
  const debouncedPassword = useDebounceValue(passwordValue, 300);

  const hasActiveRules =
    isStrengthEnabled &&
    !flagLoading &&
    !flagError &&
    !rulesLoading &&
    rules.length > 0;

  const passwordScore = hasActiveRules
    ? rules.filter(rule => {
        try {
          return new RegExp(rule.regex).test(debouncedPassword);
        } catch {
          return false;
        }
      }).length
    : 0;

  const allRulesPassed = hasActiveRules && passwordScore === rules.length;

  const strengthLabel =
    hasActiveRules && passwordScore > 0
      ? t(
          ALL_STRENGTH_LABELS_ORDER[
            Math.max(0, 4 - rules.length + passwordScore - 1)
          ],
        )
      : undefined;

  const requirementsTitle =
    rules.length === 1
      ? t('CHANGE_PASSWORD_STRENGTH_REQUIREMENT_TITLE_SINGULAR')
      : t('CHANGE_PASSWORD_STRENGTH_REQUIREMENTS_TITLE');

  const onSubmit = async (data: TChangePasswordSchema) => {
    if (isStrengthEnabled) {
      // FF-enabled path: changeMyPassword resolves user/org IDs server-side
      // from the JWT, so no learning session data is required.
      const response = await changeMyPassword({
        variables: {
          input: { password: data.password },
        },
      });

      const payload = response.data?.security_policies.change_my_password;

      if (payload?.__typename === 'ChangeMyPasswordPayload') {
        // Server may redirect to a post-change page (e.g. org home).
        if (payload.next_url) {
          window.location.href = payload.next_url;
          return;
        }
        onSuccess();
        return;
      }

      if (
        payload?.__typename === 'SecurityMutationError' &&
        payload.errors.includes('password_unchanged')
      ) {
        setRejectedPassword(data.password);
        toast.error(t('CHANGE_PASSWORD_UNCHANGED_ERROR_TITLE'), {
          description: t('CHANGE_PASSWORD_UNCHANGED_ERROR_DESCRIPTION'),
        });
        return;
      }
    } else {
      // Legacy path: changePassword requires explicit IDs from the learning
      // session. Admin users without a learning session can't use this path —
      // they must wait for the FF to be enabled.
      if (!learningSessionData) {
        form.setError('password', {
          type: 'manual',
          message: t('CHANGE_PASSWORD_ERROR_MESSAGE'),
        });
        return;
      }

      const response = await changePassword({
        variables: {
          input: {
            password: data.password,
            confirm_password: data.confirmPassword,
            user_id: learningSessionData.userLearningId,
            organization_id: learningSessionData.organizationLearningId,
          },
        },
      });

      if (
        response.data?.security.change_password_user.__typename ===
        'ChangePasswordUserChangedPasswordPayload'
      ) {
        onSuccess();
        return;
      }
    }

    toast.error(t('CHANGE_PASSWORD_ERROR_MESSAGE'));
  };

  // Guard is intentionally placed here (after FF resolves) and not in the outer
  // ChangePasswordForm wrapper. Moving it outward caused admin users — who have
  // no learning session — to be redirected back to the login page in an
  // infinite loop when the FF was enabled for their org, because the wrapper
  // blocked render before isStrengthEnabled was known.
  if (
    !flagLoading &&
    !flagError &&
    !isStrengthEnabled &&
    !learningSessionData
  ) {
    return (
      <div>
        <p className="tw-cds-font-body-01 tw-font-semibold tw-mb-1.2 tw-text-alert-500">
          {t('LEARNING_SESSION_NOT_FOUND')}
        </p>
        <Button type="button" onClick={() => window.location.reload()}>
          {t('RELOAD_PAGE')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="tw-mb-4 md:tw-mb-2.4">
        <h3 className="tw-cds-font-heading-02 tw-mb-1.2">
          {t('CHANGE_PASSWORD_FORM_TITLE')}
        </h3>
        <p className="tw-cds-font-body-02">
          {t('CHANGE_PASSWORD_FORM_DESCRIPTION')}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="tw-space-y-2.4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('CHANGE_PASSWORD_FORM_INPUT_PASSWORD_LABEL')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('CHANGE_PASSWORD_FORM_INPUT_PASSWORD_HINT')}
                    value={field.value}
                    onChange={e => {
                      field.onChange(e);
                      // Validate password live (onTouched only fires after blur)
                      // so the no-spaces / min error surfaces while typing.
                      trigger('password');
                      if (getValues('confirmPassword').length > 0) {
                        trigger('confirmPassword');
                      }
                    }}
                  />
                </FormControl>

                {/* Always render the field error. In strength mode the rules
                    list covers the org rules, but the zod schema still guards
                    empty/space-containing passwords (min + no-spaces regex) —
                    those errors must stay visible so the user knows why submit
                    is blocked (e.g. "no puede contener espacios"). */}
                <FormMessage />

                {isStrengthEnabled && flagLoading && (
                  <div className="tw-flex tw-flex-col tw-gap-0.8 tw-mt-1.2">
                    <div className="tw-w-full tw-h-0.4 tw-rounded-0.4 tw-cds-bg-subtle-01 tw-animate-pulse" />
                    <div className="tw-w-2/3 tw-h-1.6 tw-rounded-0.4 tw-cds-bg-subtle-01 tw-animate-pulse" />
                  </div>
                )}

                {isStrengthEnabled && !flagLoading && flagError && (
                  <button
                    type="button"
                    className="tw-cds-font-label-01 tw-cds-text-interactive-primary tw-mt-1.2"
                    onClick={handleFlagRetry}
                  >
                    {t('CHANGE_PASSWORD_STRENGTH_RETRY')}
                  </button>
                )}

                {hasActiveRules && (
                  <>
                    <PasswordStrengthBar
                      score={passwordScore}
                      total={rules.length}
                      visible={passwordValue.length > 0}
                      label={strengthLabel}
                    />
                    <PasswordRulesList
                      rules={rules}
                      password={debouncedPassword}
                      title={requirementsTitle}
                    />
                  </>
                )}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('CHANGE_PASSWORD_FORM_INPUT_CONFIRM_PASSWORD_LABEL')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t(
                      'CHANGE_PASSWORD_FORM_INPUT_CONFIRM_PASSWORD_HINT',
                    )}
                    value={field.value}
                    onChange={e => {
                      field.onChange(e);
                      trigger('confirmPassword');
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            variant="primary"
            size="md"
            type="submit"
            disabled={
              !isValid ||
              (hasActiveRules && !allRulesPassed) ||
              isSubmitting ||
              (isStrengthEnabled &&
                rejectedPassword !== undefined &&
                passwordValue === rejectedPassword)
            }
            loading={isSubmitting}
            className="tw-w-full tw-justify-center"
          >
            {t(
              isSubmitting
                ? 'CHANGE_PASSWORD_FORM_BUTTON_SUBMIT_LABEL_LOADING'
                : 'CHANGE_PASSWORD_FORM_BUTTON_SUBMIT_LABEL',
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

const ChangePasswordFormWithFlag = (props: TChangePasswordFormContentProps) => {
  // ENABLE_PASSWORD_STRENGTH is provisioned per LEARNING org id (not the
  // centralized id) — seed the flag provider with props.organizationId
  // (orgInfo.originalId, the learning org id resolved on the login page).
  const learningOrgId = props.organizationId
    ? parseInt(props.organizationId, 10)
    : undefined;

  if (!learningOrgId) {
    return <ChangePasswordFormContent {...props} />;
  }

  return (
    <FeatureFlagProvider
      organizationId={learningOrgId}
      flags={[FEATURE_FLAG_ENABLE_PASSWORD_STRENGTH]}
    >
      <ChangePasswordFormContent {...props} />
    </FeatureFlagProvider>
  );
};

export const ChangePasswordForm = ({
  onSuccess,
  organizationId,
}: TChangePasswordFormProps) => {
  const { t } = useTranslation();
  const { learningSessionData, loading, error, refetch } = useLearningSession();

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-8">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <p className="tw-cds-font-body-01 tw-font-semibold tw-mb-1.2 tw-text-alert-500">
          {t('LEARNING_SESSION_QUERY_ERROR')}
        </p>
        <Button type="button" onClick={() => refetch()}>
          {t('RETRY')}
        </Button>
      </div>
    );
  }

  return (
    <ChangePasswordFormWithFlag
      onSuccess={onSuccess}
      organizationId={organizationId}
      learningSessionData={learningSessionData}
    />
  );
};
